import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { MiniTopic } from '@deepguide-ai/types'
import {
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui'
import { Separator } from '@deepguide-ai/ui'
import { Switch } from '@deepguide-ai/ui'
import { Button } from '@deepguide-ai/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@deepguide-ai/ui'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper } from '@tiptap/react'
import { useAtom } from 'jotai'
import { ArrowRight, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DocIcon } from '@/components/ui/doc-icon'
import {
  SettingsField,
  SettingsPopoverHeader,
  SettingsToggle,
} from '@/tiptap/tiptap-ui/settings-popover/settings-popover'
import {
  useFolderContentsData,
  useWorkspaceFromResolver,
} from '../../hooks/useExtensionData'
import { FolderContentsAttributes } from './folder-contents-extension'

interface FolderContentsNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function FolderContentsNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: FolderContentsNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const navigate = useNavigate()

  const attrs = node.attrs as FolderContentsAttributes

  // Get context data - this will be populated by the data resolver
  // The selectedTopicId should be stored in node attributes for static export compatibility
  let selectedTopicId = attrs.selectedTopicId || ''

  // If selectedTopicId is empty, try to get it from the current topic context from the URL
  // This helps with existing folder contents that were created before the selectedTopicId fix
  if (!selectedTopicId) {
    // Try to extract topic ID from current URL as fallback
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const topicIdFromUrl = urlParams.get('topicId')
      if (topicIdFromUrl) {
        selectedTopicId = topicIdFromUrl
        console.log(
          '📁 [FolderContentsNodeView] Using topicId from URL as fallback:',
          selectedTopicId
        )

        // Update the node attributes to persist this for future use
        if (editor.isEditable) {
          updateAttributes({ selectedTopicId })
        }
      }
    } catch (error) {
      console.warn(
        '📁 [FolderContentsNodeView] Failed to extract topicId from URL:',
        error
      )
    }
  }
  const workspace = useWorkspaceFromResolver()
  const layout = attrs.layout || 'grid-2'
  const showIcons = attrs.showIcons !== false
  const showDescriptions = attrs.showDescriptions !== false
  const selectedItems = attrs.selectedItems || []
  const customOrder = attrs.customOrder || []
  const serializedTopicData = attrs.topicData || []

  // Temporary state for popover editing
  const [tempLayout, setTempLayout] = useState(layout)
  const [tempShowIcons, setTempShowIcons] = useState(showIcons)
  const [tempShowDescriptions, setTempShowDescriptions] =
    useState(showDescriptions)
  const [tempSelectedItems, setTempSelectedItems] =
    useState<string[]>(selectedItems)

  // Use the new data resolver hook
  const {
    data: resolvedTopics,
    isLoading: isLoadingTopics,
    error: topicsError,
    refetch,
  } = useFolderContentsData({
    selectedTopicId,
    selectedItems,
    customOrder,
    fallbackData: serializedTopicData,
    includeFallback: true,
    enabled: true,
  })

  // Force refetch when selectedTopicId changes to ensure fresh data
  useEffect(() => {
    if (selectedTopicId) {
      refetch()
    }
  }, [selectedTopicId, refetch])

  // Debug logging
  useEffect(() => {
    console.log('📁 [FolderContentsNodeView] Data resolver result:', {
      selectedTopicId,
      resolvedTopicsCount: resolvedTopics.length,
      isLoadingTopics,
      topicsError: topicsError?.message,
      fallbackDataCount: serializedTopicData.length,
    })
  }, [
    selectedTopicId,
    resolvedTopics,
    isLoadingTopics,
    topicsError,
    serializedTopicData,
  ])

  // Get available subtopics for the current topic
  const availableSubtopics = useMemo(() => {
    // Use resolved topics from the data resolver
    return resolvedTopics
  }, [resolvedTopics])

  // Get displayed subtopics (processing is now handled by the data resolver)
  const displayedSubtopics = useMemo(() => {
    return availableSubtopics
  }, [availableSubtopics])

  // Function to update folder contents attributes
  const updateFolderContentsAttributes = useCallback(
    (newAttributes: Partial<FolderContentsAttributes>) => {
      try {
        // Store current topic data for static serialization when updating
        const attributesWithTopicData = {
          ...newAttributes,
          topicData:
            availableSubtopics.length > 0
              ? availableSubtopics
              : serializedTopicData,
        }

        updateAttributes(attributesWithTopicData)

        console.log(
          'Successfully updated folder contents attributes:',
          attributesWithTopicData
        )
      } catch (error) {
        console.error('Failed to update folder contents attributes:', error)
      }
    },
    [updateAttributes, availableSubtopics, serializedTopicData]
  )

  // Auto-capture topic data when resolved topics change (for static serialization)
  useEffect(() => {
    if (resolvedTopics.length > 0 && editor.isEditable) {
      // Only update if the resolved data is different from serialized data
      const currentTopicDataJson = JSON.stringify(serializedTopicData)
      const resolvedTopicsJson = JSON.stringify(resolvedTopics)

      if (currentTopicDataJson !== resolvedTopicsJson) {
        console.log('Auto-capturing resolved topic data:', resolvedTopics)
        updateFolderContentsAttributes({ topicData: resolvedTopics })
      }
    }
  }, [
    resolvedTopics,
    serializedTopicData,
    updateFolderContentsAttributes,
    editor.isEditable,
  ])

  // Function to save settings from popover
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<FolderContentsAttributes> = {}

    if (tempLayout !== layout) {
      newAttributes.layout = tempLayout
    }

    if (tempShowIcons !== showIcons) {
      newAttributes.showIcons = tempShowIcons
    }

    if (tempShowDescriptions !== showDescriptions) {
      newAttributes.showDescriptions = tempShowDescriptions
    }

    if (JSON.stringify(tempSelectedItems) !== JSON.stringify(selectedItems)) {
      newAttributes.selectedItems = tempSelectedItems
    }

    if (Object.keys(newAttributes).length > 0) {
      updateFolderContentsAttributes(newAttributes)
    }

    setIsPopoverOpen(false)
  }, [
    tempLayout,
    layout,
    tempShowIcons,
    showIcons,
    tempShowDescriptions,
    showDescriptions,
    tempSelectedItems,
    selectedItems,
    updateFolderContentsAttributes,
  ])

  // Function to cancel settings changes
  const cancelSettings = useCallback(() => {
    setTempLayout(layout)
    setTempShowIcons(showIcons)
    setTempShowDescriptions(showDescriptions)
    setTempSelectedItems(selectedItems)
    setIsPopoverOpen(false)
  }, [layout, showIcons, showDescriptions, selectedItems])

  // Function to open settings popover
  const openSettings = useCallback(() => {
    setTempLayout(layout)
    setTempShowIcons(showIcons)
    setTempShowDescriptions(showDescriptions)
    setTempSelectedItems(selectedItems)
    setIsPopoverOpen(true)
  }, [layout, showIcons, showDescriptions, selectedItems])

  // Handle deleting the folder contents element
  const handleDeleteElement = useCallback(() => {
    if (!editor.isEditable) return
    deleteNode()
  }, [editor.isEditable, deleteNode])

  // Handle navigation to a topic
  const handleTopicNavigation = useCallback(
    (topic: MiniTopic) => {
      if (!workspace) {
        console.warn('No workspace available for navigation')
        return
      }

      navigate({
        to: '/workspaces/$workspaceId/docs/$' as const,
        params: { workspaceId: workspace.slug!, _splat: topic.canonicalSlug },
        search: { docId: topic.docId, topicId: topic.id },
      })
    },
    [navigate, workspace]
  )

  // Toggle item selection
  const toggleItemSelection = useCallback((topicId: string) => {
    setTempSelectedItems((prev) => {
      if (prev.includes(topicId)) {
        return prev.filter((id) => id !== topicId)
      } else {
        return [...prev, topicId]
      }
    })
  }, [])

  // Layout styles
  const getLayoutStyles = () => {
    switch (layout) {
      case 'grid-3':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      case 'list':
        return 'grid-cols-1'
      case 'grid-2':
      default:
        return 'grid-cols-1 md:grid-cols-2'
    }
  }

  return (
    <NodeViewWrapper
      className={cn(
        'group/folder-contents relative p-0',
        selected && 'ProseMirror-selectednode'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='folder-contents-container relative'>
        {/* Settings button - appears on hover */}
        {editor.isEditable && (isHovered || isPopoverOpen) && (
          <div className='absolute top-3 right-3 z-10'>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='ghost'
                  size='menuAction'
                  onClick={(e) => {
                    e.stopPropagation()
                    openSettings()
                  }}
                  className='opacity-60 hover:opacity-100'
                >
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className='w-80 p-4 pb-3'
                align='end'
                side='bottom'
                sideOffset={8}
              >
                <div className='space-y-4'>
                  <SettingsPopoverHeader
                    title='Folder Contents Settings'
                    onClose={cancelSettings}
                  />

                  <div className='space-y-5'>
                    <SettingsField label='Layout'>
                      <Select value={tempLayout} onValueChange={setTempLayout}>
                        <SelectTrigger className='mb-2 w-full'>
                          <SelectValue placeholder='Select Icon Type' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='grid-2'>2 Column Grid</SelectItem>
                          <SelectItem value='grid-3'>3 Column Grid</SelectItem>
                          <SelectItem value='list'>1 Column List</SelectItem>
                        </SelectContent>
                      </Select>
                    </SettingsField>

                    <SettingsToggle label='Show Article Icons'>
                      <Switch
                        checked={tempShowIcons}
                        onCheckedChange={setTempShowIcons}
                      />
                    </SettingsToggle>

                    <SettingsToggle label='Show Article Descriptions'>
                      <Switch
                        checked={tempShowDescriptions}
                        onCheckedChange={setTempShowDescriptions}
                      />
                    </SettingsToggle>

                    {/* Item Selection */}
                    {availableSubtopics?.length > 0 && (
                      <SettingsField
                        label='Select Items to Display (If none selected, all items will be displayed)'
                      >
                        <div className='max-h-48 space-y-2 overflow-y-auto rounded-md border p-3'>
                          {availableSubtopics?.map((topic) => (
                            <div
                              key={topic.id}
                              className='flex items-center gap-3'
                            >
                              {/* <input
                              type='checkbox'
                              id={`topic-${topic.id}`}
                              checked={tempSelectedItems.includes(topic.id)}
                              onChange={() => toggleItemSelection(topic.id)}
                              className='rounded-sm'
                            />
                            <label
                              htmlFor={`topic-${topic.id}`}
                              className='cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                            >
                              {topic.title}
                            </label> */}
                              <Checkbox
                                id={`topic-${topic.id}`}
                                checked={tempSelectedItems.includes(topic.id)}
                                onChange={() => toggleItemSelection(topic.id)}
                              />
                              <Label htmlFor={`topic-${topic.id}`}>
                                {topic.title}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </SettingsField>
                    )}
                  </div>

                  <Separator className='!mt-5 !mb-3 -ml-4 !w-[calc(100%+32px)]' />

                  <div className='flex items-center justify-between'>
                    <Button
                      variant='destructive'
                      className='h-8 !bg-transparent px-2 py-1.5 text-[var(--destructive)] shadow-none'
                      onClick={handleDeleteElement}
                    >
                      Delete
                    </Button>
                    <div className='flex items-center space-x-2'>
                      <Button
                        variant='outline'
                        className='h-8 px-2 py-1.5'
                        onClick={cancelSettings}
                      >
                        Cancel
                      </Button>
                      <Button
                        className='h-8 px-2 py-1.5'
                        onClick={saveSettings}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Contents Display */}
        {isLoadingTopics ? (
          <div className='text-muted-foreground py-8 text-center'>
            <p className='text-sm'>Loading folder contents...</p>
          </div>
        ) : topicsError ? (
          <div className='text-muted-foreground py-8 text-center'>
            <p className='text-sm text-status-red'>
              Failed to load folder contents
            </p>
            <p className='mt-1 text-xs'>
              {topicsError.message || 'An error occurred while loading data'}
            </p>
          </div>
        ) : displayedSubtopics.length > 0 ? (
          <div className={cn('grid gap-4', getLayoutStyles())}>
            {displayedSubtopics.map((topic) => (
              <div
                key={topic.id}
                className='cursor-pointer rounded-md border p-4 transition-shadow hover:shadow-md'
                onClick={() => handleTopicNavigation(topic)}
              >
                <div className='flex items-start gap-3'>
                  {showIcons && (
                    <DocIcon
                      topic={topic}
                      size={20}
                      className={`flex-shrink-0 ${
                        topic.iconCategory === 'EMOJI'
                          ? 'relative -top-[3px] !block size-5'
                          : ''
                      }`}
                    />
                  )}

                  <div className='min-w-0 flex-1'>
                    <h4
                      className={cn(
                        'mb-1 truncate text-sm leading-5 font-medium',
                        showDescriptions && topic.description ? 'mb-1' : '!mb-0'
                      )}
                    >
                      {topic.title}
                    </h4>

                    {showDescriptions && topic.description && (
                      <p className='text-muted-foreground line-clamp-2 text-xs leading-4'>
                        {topic.description}
                      </p>
                    )}

                    {layout === 'list' && (
                      <div className='mt-2 flex items-center justify-between'>
                        <span className='text-muted-foreground text-xs'>
                          {topic.type === 'folder' ? 'Folder' : 'Document'}
                        </span>
                        <ArrowRight className='text-muted-foreground h-3 w-3' />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='text-muted-foreground border-border bg-secondary/45 rounded-md border-[1.5px] border-dashed py-8 text-center'>
            <p className='text-sm'>No items to display</p>
            <p className='!mt-1 !text-sm'>
              This folder doesn't contain any subdocuments or subfolders.
            </p>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  )
}
